import {
  genealogyImportPreview,
  genealogyImportSheet,
  getGenealogyImportSampleUrl,
  useGenealogyImportRun,
} from "@api/generated/genealogy-import/genealogy-import";
import type { GenealogyImportDraft, GenealogyMappingOutput as GenealogyMapping, GenealogyRow, Problem } from "@api/generated/model";
import { useUploadsUpload } from "@api/generated/uploads/uploads";
import { usePreview } from "@app/preview";
import { useAutosave, useSeasonFromLink, useSettings } from "@app/workspace";
import { ResultBanner } from "@features/system/undo";
import { Button, FileButton, Loader, NumberInput, SegmentedControl, Select, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { DropZone } from "@ui/DropZone";
import { formatNumber, num } from "@ui/format";
import { Kpi } from "@ui/Kpi";
import { notifyError } from "@ui/notify";
import { PageHeader } from "@ui/PageHeader";
import { QueryFeedback } from "@ui/QueryFeedback";
import { Steps } from "@ui/Steps";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

type Target = string; // the server lists them (GenealogySheet.targets)
type Values = GenealogyMapping["columns"];
const ACCEPT = ".xlsx,.xls,.xlsm,.xlsb,.ods,.csv,.tsv,.txt,.mdb,.accdb,.dbf,.zip";
const STEPS = ["file", "columns", "check", "load"] as const;
const MAPPING: GenealogyMapping = { sheet: null, header_row: 1, mode: "register", columns: {}, fixed: {} };
const STATUS_CHIP: Record<GenealogyRow["status"], "g" | "y" | "b"> = { existing: "g", new: "y", same_new: "y", new_sid: "b", sibling: "b" };
// the register fields are labelled as on the register page (torzs.f); the pedigree and the plot fields here
const REGISTER_LABEL = new Set([
  "torzs_nev",
  "fajta",
  "torzs_szam",
  "rov",
  "pgm",
  "gh",
  "orsz",
  "ceg",
  "reg",
  "canc",
  "torzs_megj",
  "mta_gene_bank",
]);

export const Route = createFileRoute("/genealogy/import")({
  validateSearch: z.object({ ev: z.number().int().optional() }),
  component: GenealogyImportPage,
});

/** Genealógia → Genealógiai import (the legacy Data Import): a sheet of material — pedigrees, line names, varieties
 *  and their register fields, optionally their plots in the working season — read by a column mapping, previewed row
 *  by row (the register line it is, or the new combination / sister line it becomes) and loaded as one undoable
 *  operation. The file and the mapping are the user's draft (``genealogy_import_draft``). */
function GenealogyImportPage() {
  const settings = useSettings();
  if (!settings) return <Loader size="sm" m="md" />;
  return <GenealogyImport stored={settings.genealogy_import_draft ?? null} />;
}

/** A map with ``key`` set to ``value``, or without it when the value is empty. */
function withKey(map: Values, key: Target, value: string | null): Values {
  const next = { ...map };
  if (value?.trim()) next[key] = value;
  else delete next[key];
  return next;
}

function GenealogyImport({ stored }: { stored: GenealogyImportDraft | null }) {
  const { t } = useTranslation();
  const season = useSeasonFromLink(Route.useSearch().ev);
  const [draft, setDraft] = useState<GenealogyImportDraft>(stored ?? { upload: null, mapping: MAPPING });
  useAutosave("genealogy_import_draft", draft, stored);
  const m = draft.mapping;
  const setMapping = (p: Partial<GenealogyMapping>) => setDraft((d) => ({ ...d, mapping: { ...d.mapping, ...p } }));
  const upload = draft.upload;
  const sheet = useQuery({
    queryKey: ["genealogy-import-sheet", upload?.id, m.sheet, m.header_row],
    queryFn: () => genealogyImportSheet({ upload: upload?.id ?? "", sheet: m.sheet, header_row: m.header_row }),
    enabled: !!upload,
    placeholderData: keepPreviousData,
    staleTime: Number.POSITIVE_INFINITY, // an upload never changes
    retry: false,
  });
  const info = upload ? sheet.data : undefined;
  useEffect(() => {
    if (!sheet.error) return;
    notifyError(sheet.error);
    setDraft((d) => ({ ...d, upload: null })); // gone (kept a week) or unreadable
  }, [sheet.error]);
  // a new file: the columns its headers name
  useEffect(() => {
    if (!info) return;
    setDraft((d) =>
      Object.keys(d.mapping.columns).length || Object.keys(d.mapping.fixed).length
        ? d
        : { ...d, mapping: { ...d.mapping, columns: info.guess } },
    );
  }, [info]);
  const uploader = useUploadsUpload({
    mutation: {
      onSuccess: ([f]) => {
        if (f) setDraft({ upload: { id: f.id, name: f.name }, mapping: { ...MAPPING, mode: m.mode } });
      },
    },
  });
  const uploadFile = (f: File | null | undefined) => {
    if (f) uploader.mutate({ data: { files: [f] } });
  };
  const plots = m.mode === "plots";
  const run = useGenealogyImportRun({
    mutation: {
      onSuccess: (out) => {
        setDraft((d) => ({ ...d, upload: null })); // the file is loaded: the mapping stays for the next one
        notifications.show({ color: "green", message: t("genealogyImport.loaded", { count: out.counts.rows }) });
      },
    },
  });
  const names = info?.targets.names ?? [];
  const request =
    info && upload && names.some((n) => m.columns[n] || m.fixed[n]) ? { upload: upload.id, mapping: m, ev: plots ? season : null } : null;
  const preview = usePreview("genealogy-import-preview", request, (body, signal) => genealogyImportPreview(body, { signal }), 400);
  const result = preview.data;
  const c = result?.counts;
  const label = useCallback(
    (target: string) => (REGISTER_LABEL.has(target) ? t(`register.field.${target}`) : t(`genealogyImport.fields.${target}`)),
    [t],
  );
  const done = run.isSuccess && !upload;
  const step = done ? 4 : result ? 3 : info ? 2 : 1;
  const columns = useMemo<Column<GenealogyRow>[]>(
    () => [
      { id: "row", header: t("genealogyImport.col.row"), value: (r) => r.number, text: (r) => String(r.number), numeric: true },
      {
        id: "names",
        header: t("genealogyImport.col.names"),
        value: (r) => [r.variety, r.name, r.pedigree].filter(Boolean).join(" · "),
        className: "sm",
      },
      {
        id: "line",
        header: t("genealogyImport.col.line"),
        value: (r) => statusText(r, t, label),
        cell: (r) => <RowStatus row={r} label={label} />,
      },
      ...(plots
        ? [
            {
              id: "plot",
              header: t("genealogyImport.col.plot"),
              value: (r) => r.plot,
              className: "mono sm",
            } satisfies Column<GenealogyRow>,
          ]
        : []),
    ],
    [t, plots, label],
  );
  // the preview lists only its first rows (problems first) when the file has more
  const table = useDataTable("genealogy.import.preview", columns, result?.rows, {
    partial: !!result && result.rows.length < result.counts.rows,
  });
  const targets = info ? [...names, ...(info.targets.line ?? []), ...(plots ? (info.targets.plots ?? []) : [])] : [];
  return (
    <div className="page fill">
      <PageHeader
        title={t("genealogyImport.title")}
        subtitle={t("genealogyImport.subtitle")}
        season={plots}
        actions={<Steps names={STEPS.map((n) => t(`genealogyImport.steps.${n}`))} step={step} />}
      />
      <div className="hstack grow" style={{ gap: 16, alignItems: "stretch", minHeight: 0 }}>
        <div className="stack" style={{ gap: 14, width: 420, flexShrink: 0, overflowY: "auto" }}>
          {/* what the file is for comes first: it decides which columns the sheet needs */}
          <section className="card pad stack" style={{ gap: 8 }}>
            <span className="sec">{t("genealogyImport.whatToLoad")}</span>
            <SegmentedControl
              size="xs"
              value={m.mode}
              onChange={(v) => setMapping({ mode: v as GenealogyMapping["mode"] })}
              data={[
                { value: "register", label: t("genealogyImport.mode.register") },
                { value: "plots", label: t("genealogyImport.mode.plots", { ev: season ?? "" }) },
              ]}
            />
            <span className="sm mu">{t(`genealogyImport.modeHelp.${m.mode}`, { ev: season ?? "" })}</span>
            <a className="sm" href={getGenealogyImportSampleUrl({ mode: m.mode })}>
              {t("genealogyImport.sampleFile")}
            </a>
          </section>
          {!upload ? (
            <DropZone
              onFiles={(files) => uploadFile(files[0])}
              busy={uploader.isPending}
              title={t("genealogyImport.dropFile")}
              hint={t("genealogyImport.fileHelp")}
              button={t("genealogyImport.selectFile")}
              accept={ACCEPT}
            />
          ) : (
            <section className="card pad stack" style={{ gap: 10 }}>
              <div className="hstack" style={{ gap: 8 }}>
                <b className="grow" style={{ overflowWrap: "anywhere" }}>
                  {upload.name}
                </b>
                <FileButton accept={ACCEPT} onChange={uploadFile}>
                  {(props) => (
                    <Button size="xs" variant="default" loading={uploader.isPending} {...props}>
                      {t("genealogyImport.otherFile")}
                    </Button>
                  )}
                </FileButton>
              </div>
              <div className="hstack" style={{ gap: 8 }}>
                {info?.sheets.some(Boolean) && (
                  <Select
                    size="xs"
                    className="grow"
                    label={t("genealogyImport.sheet")}
                    data={info.sheets}
                    value={m.sheet ?? info.sheet}
                    allowDeselect={false}
                    onChange={(v) => setMapping({ sheet: v })}
                  />
                )}
                <NumberInput
                  size="xs"
                  w={110}
                  label={t("genealogyImport.headerRow")}
                  min={1}
                  max={1000}
                  value={m.header_row}
                  onChange={(v) => setMapping({ header_row: num(v) ?? 1 })}
                />
              </div>
              {info && <span className="sm mu">{t("genealogyImport.rowCount", { count: formatNumber(info.rows) })}</span>}
            </section>
          )}
          {info && (
            <section className="card pad stack" style={{ gap: 6 }}>
              <span className="sec">{t("genealogyImport.mapping")}</span>
              <p className="sm mu">{t("genealogyImport.mappingHelp")}</p>
              {targets.map((target) => (
                <div key={target} className="hstack" style={{ gap: 6, alignItems: "flex-end" }}>
                  <Select
                    size="xs"
                    className="grow"
                    label={label(target)}
                    placeholder={t("genealogyImport.noColumn")}
                    data={info.columns}
                    value={m.columns[target] ?? null}
                    clearable
                    searchable
                    onChange={(v) => setMapping({ columns: withKey(m.columns, target, v) })}
                  />
                  <TextInput
                    size="xs"
                    w={120}
                    aria-label={t("genealogyImport.fixedFor", { field: label(target) })}
                    placeholder={t("genealogyImport.fixedValue")}
                    disabled={!!m.columns[target]}
                    value={m.fixed[target] ?? ""}
                    onChange={(e) => setMapping({ fixed: withKey(m.fixed, target, e.currentTarget.value) })}
                  />
                </div>
              ))}
            </section>
          )}
        </div>
        <div className="stack grow" style={{ gap: 14, minWidth: 0 }}>
          {info && !request && <p className="note sm">{t("genealogyImport.nameRequired")}</p>}
          {preview.busy && !result && <Loader size="sm" />}
          <QueryFeedback busy={preview.busy} />
          {preview.error && !preview.busy && <p className="note err sm">{(preview.error as Error).message}</p>}
          {c && result && (
            <>
              <div className="kpis">
                <Kpi label={t("genealogyImport.stats.rows")} value={formatNumber(c.rows)} />
                <Kpi label={t("genealogyImport.stats.existing")} value={formatNumber(c.existing)} />
                <Kpi label={t("genealogyImport.stats.newCombination")} value={formatNumber(c.combinations)} />
                <Kpi label={t("genealogyImport.stats.newLine")} value={formatNumber(c.lines)} />
                <Kpi label={t("genealogyImport.stats.invalid")} value={formatNumber(c.refused)} className={c.refused ? "err" : undefined} />
              </div>
              <section className="card stack grow" style={{ minHeight: 0, gap: 0, opacity: preview.busy ? 0.6 : 1 }}>
                <div className="hstack panel-head" style={{ gap: 8 }}>
                  <span className="sec grow">{t("genealogyImport.preview")}</span>
                  {c.ambiguous > 0 && <span className="chip y">{t("genealogyImport.multipleMatches", { count: c.ambiguous })}</span>}
                  <Button
                    size="xs"
                    disabled={c.refused > 0 || !c.rows || preview.busy}
                    loading={run.isPending}
                    onClick={() => request && run.mutate({ data: { ...request, token: result.token } })}
                  >
                    {t("genealogyImport.load", { count: c.rows })}
                  </Button>
                </div>
                <DataTable table={table} rowKey={(r) => r.number} className="grow" />
                {result.rows.length < c.rows && (
                  <p className="sm mu pad">{t("genealogyImport.more", { count: formatNumber(c.rows - result.rows.length) })}</p>
                )}
              </section>
            </>
          )}
          {done && run.data && (
            <ResultBanner operation={run.data.operation}>
              <b>{t("genealogyImport.done")}</b>
            </ResultBanner>
          )}
        </div>
      </div>
    </div>
  );
}

/** What a preview row is or becomes, in words: the chip's text, which the column sorts and filters by. */
function statusText(row: GenealogyRow, t: TFunction, label: (target: string) => string): string {
  const problem = (p: Problem) => t(`genealogyImport.problems.${p.code}`, { field: p.target ? label(p.target) : "" });
  if (row.problems.length) return row.problems.map(problem).join(", ");
  const status = t(`genealogyImport.status.${row.status}`, { name: row.current ?? "—", paz: row.paz, sid: row.sid, n: row.line });
  return row.ambiguous ? `${status} · ${t("genealogyImport.multipleMatchHint")}` : status;
}

function RowStatus({ row, label }: { row: GenealogyRow; label: (target: string) => string }) {
  const { t } = useTranslation();
  return <span className={`chip ${row.problems.length ? "r" : STATUS_CHIP[row.status]}`}>{statusText(row, t, label)}</span>;
}
